import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import * as XLSX from 'xlsx';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { CityParkingCards } from '../../../../core/models/parking-card.model';
import {
  IdNameOption,
  LookupParkingCardFoundDto,
  LookupParkingCardNotFoundDto,
  LookupParkingCardsByLicensesDto,
} from '../../../../core/models/parking/parking-card-list.model';
import { MapperParkingCardListService } from '../../../../core/mappers/parking/mapper-parking-card-list.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ApiService } from '../../../../core/services/api.service';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { ParkingVehicleTypeResponse } from '../../../../core/models/vehicle.model';

type BulkEditMode = 'dates' | 'subscription' | 'client';

interface ExcelLicenseItem {
  license: string;
  vehicleTypeId?: number;
  vehicleTypeName?: string;
}

interface SubscriptionOption extends IdNameOption {
  vehicleTypeId?: number;
}

interface LookupFoundGroup {
  vehicleTypeId: number;
  vehicleTypeName: string;
  rows: ExcelLookupFoundRow[];
}

interface ExcelLookupFoundRow {
  inputLicense: string;
  normalizedLicense: string;
  cardId: number;
  cardName: string;
  cardCode: string;
  plateNumber: string;
  clientName: string;
  memberName: string;
  subscriptionId: number;
  subscriptionName: string;
  vehicleTypeId: number;
  vehicleTypeName: string;
  issueDate: string | null;
  expireDate: string | null;
  status: number;
}

@Component({
  selector: 'app-parking-cards-bulk-dates',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent],
  templateUrl: './parking-cards-bulk-dates.component.html',
  styleUrl: './parking-cards-bulk-dates.component.css',
  providers: [DatePipe],
})
export class ParkingCardsBulkDatesComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly apiService = inject(ApiParkingCardListService);
  private readonly apiLegacy = inject(ApiService);
  private readonly mapper = inject(MapperParkingCardListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  parkingCards: CityParkingCards[] = [];
  selectedIds = new Set<number>();

  searchText = '';
  searchType: 'card_name' | 'license' = 'card_name';
  filterStatus = 0;

  clients: ClientIInfo[] = [];
  selectedClientId = 0;
  clientSearchText = '';
  isClientDropdownOpen = false;

  vehicleTypes: ParkingVehicleTypeResponse[] = [];
  selectedVehicleTypeId = 0;
  vehicleTypeSearchText = '';
  isVehicleTypeDropdownOpen = false;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  isLoading = false;
  isSaving = false;
  isLookingUp = false;
  isEditModalOpen = false;
  isConfirmModalOpen = false;
  lastFetchedAt: Date | null = null;

  excelFileName = '';
  excelLicenseItems: ExcelLicenseItem[] = [];
  lookupFound: ExcelLookupFoundRow[] = [];
  lookupNotFound: LookupParkingCardNotFoundDto[] = [];
  subscriptions: SubscriptionOption[] = [];

  editMode: BulkEditMode | null = null;
  /** บัตรที่จะบันทึกในรอบ modal นี้ (จากกลุ่มประเภทรถ หรือการเลือกทั่วไป) */
  editScopeCardIds: number[] = [];
  editIssueDate = '';
  editExpireDate = '';
  editSubscriptionId = 0;
  editClientId = 0;
  editClientSearchText = '';
  isEditClientDropdownOpen = false;

  userId = 0;
  protected Math = Math;

  private static readonly CLIENT_OPTIONS_PREVIEW = 20;

  constructor() {
    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;
      untracked(() => {
        this.currentPage = 1;
        this.selectedIds.clear();
        this.clearExcelLookup();
        this.closeEditModal();
        this.loadClients();
        this.loadVehicleTypes();
        this.loadSubscriptions();
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get hasExcelLookup(): boolean {
    return this.lookupFound.length > 0 || this.lookupNotFound.length > 0;
  }

  get confirmHighlight(): string {
    const n = this.editScopeCardIds.length > 0 ? this.editScopeCardIds.length : this.selectedCount;
    return `${n} ใบ`;
  }

  get lookupFoundGroups(): LookupFoundGroup[] {
    const map = new Map<number, LookupFoundGroup>();
    for (const row of this.lookupFound) {
      const id = row.vehicleTypeId || 0;
      const name = (row.vehicleTypeName || '').trim() || 'ไม่ระบุประเภทรถ';
      let group = map.get(id);
      if (!group) {
        group = { vehicleTypeId: id, vehicleTypeName: name, rows: [] };
        map.set(id, group);
      }
      group.rows.push(row);
    }
    return [...map.values()].sort((a, b) => a.vehicleTypeName.localeCompare(b.vehicleTypeName, 'th'));
  }

  get editScopeCount(): number {
    return this.editScopeCardIds.length;
  }

  get editModalTitle(): string {
    switch (this.editMode) {
      case 'dates': return 'แก้ไขวันเริ่มสิทธิ์ / วันหมดอายุ';
      case 'subscription': return 'แก้ไข Subscription';
      case 'client': return 'แก้ไข Client';
      default: return 'แก้ไขบัตรที่เลือก';
    }
  }

  get confirmTitle(): string {
    switch (this.editMode) {
      case 'dates': return 'ยืนยันแก้ไขวันที่บัตร?';
      case 'subscription': return 'ยืนยันแก้ไข Subscription?';
      case 'client': return 'ยืนยันแก้ไข Client?';
      default: return 'ยืนยันแก้ไขบัตรที่เลือก?';
    }
  }

  get confirmMessageSuffix(): string {
    if (this.editMode === 'dates') {
      return `เป็นวันเริ่มสิทธิ์ ${this.formatDateDisplay(this.editIssueDate)} และวันหมดอายุ ${this.formatDateDisplay(this.editExpireDate)} หรือไม่? หากลานเป็น Taiwan site ระบบจะ sync ไป Taiwan ให้ด้วย`;
    }
    if (this.editMode === 'subscription') {
      const sub = this.subscriptionsForEdit.find((s) => s.id === this.editSubscriptionId);
      return `เป็น Subscription “${sub?.name || '—'}” หรือไม่?`;
    }
    if (this.editMode === 'client') {
      const client = this.clients.find((c) => c.id === this.editClientId);
      return `เป็น Client “${client?.name || '—'}” หรือไม่?`;
    }
    return 'ยืนยันการบันทึกหรือไม่?';
  }

  get subscriptionsForEdit(): SubscriptionOption[] {
    const vtId = this.singleVehicleTypeInScope;
    if (!vtId) return this.subscriptions;
    return this.subscriptions.filter((s) => !s.vehicleTypeId || s.vehicleTypeId === vtId);
  }

  get singleVehicleTypeInScope(): number | null {
    const ids = this.collectVehicleTypeIdsForCards(this.editScopeCardIds);
    if (ids.size === 1) return [...ids][0];
    return null;
  }

  get vehicleTypeFilterOptions(): ParkingVehicleTypeResponse[] {
    const sorted = [...this.vehicleTypes].sort((a, b) => a.typeName.localeCompare(b.typeName, 'th'));
    const q = this.vehicleTypeSearchText.trim().toLowerCase();
    if (!q) return sorted.slice(0, ParkingCardsBulkDatesComponent.CLIENT_OPTIONS_PREVIEW);
    return sorted.filter((v) => v.typeName.toLowerCase().includes(q));
  }

  get clientFilterOptions(): ClientIInfo[] {
    return this.filterClients(this.clientSearchText, this.selectedClientId);
  }

  get editClientOptions(): ClientIInfo[] {
    return this.filterClients(this.editClientSearchText, this.editClientId);
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get allPageSelected(): boolean {
    if (this.parkingCards.length === 0) return false;
    return this.parkingCards.every((c) => this.selectedIds.has(c.id));
  }

  get allExcelFoundSelected(): boolean {
    if (this.lookupFound.length === 0) return false;
    return this.lookupFound.every((r) => this.selectedIds.has(r.cardId));
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadClients();
      this.loadVehicleTypes();
      this.loadSubscriptions();
      this.loadTable();
    }
  }

  onRefresh(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  setFilterStatus(status: number): void {
    this.filterStatus = status;
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.currentPage = 1;
    this.loadTable();
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  toggleRow(id: number, checked: boolean): void {
    if (checked) this.selectedIds.add(id);
    else this.selectedIds.delete(id);
  }

  toggleSelectAllPage(checked: boolean): void {
    if (checked) {
      this.parkingCards.forEach((c) => this.selectedIds.add(c.id));
    } else {
      this.parkingCards.forEach((c) => this.selectedIds.delete(c.id));
    }
  }

  toggleSelectAllExcel(checked: boolean): void {
    if (checked) this.lookupFound.forEach((r) => this.selectedIds.add(r.cardId));
    else this.lookupFound.forEach((r) => this.selectedIds.delete(r.cardId));
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  clearExcelLookup(): void {
    this.excelFileName = '';
    this.excelLicenseItems = [];
    this.lookupFound = [];
    this.lookupNotFound = [];
  }

  downloadSampleExcel(): void {
    const sampleData = [
      { ทะเบียน: '1กร 1234', vehicle_type: '' },
      { ทะเบียน: 'กข 9999', vehicle_type: '' },
      { ทะเบียน: '1กก5555', vehicle_type: 'จักรยานยนต์' },
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleData, { header: ['ทะเบียน', 'vehicle_type'] });
    worksheet['!cols'] = [{ wch: 16 }, { wch: 22 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Licenses');
    XLSX.writeFile(workbook, 'ParkingCards_License_Lookup_Sample.xlsx');
  }

  downloadNotFoundExcel(): void {
    if (this.lookupNotFound.length === 0) {
      this.toast.error('ไม่มีทะเบียนที่ไม่พบให้ดาวน์โหลด');
      return;
    }

    const sampleData = this.lookupNotFound.map((n) => ({
      licensePlate: (n.inputLicense || n.normalizedLicense || '').trim(),
      vehicle_type: (n.vehicleTypeName || '').trim(),
      province: '',
      name: '',
      phone: '',
      address: '',
      reason: (n.reason || '').trim(),
    })).filter((r) => !!r.licensePlate);

    if (sampleData.length === 0) {
      this.toast.error('ไม่มีทะเบียนที่ไม่พบให้ดาวน์โหลด');
      return;
    }

    const headers = ['licensePlate', 'vehicle_type', 'province', 'name', 'phone', 'address', 'reason'];
    const worksheet = XLSX.utils.json_to_sheet(sampleData, { header: headers });
    worksheet['!cols'] = [
      { wch: 16 },
      { wch: 16 },
      { wch: 20 },
      { wch: 14 },
      { wch: 28 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'NotFound');
    XLSX.writeFile(workbook, 'ParkingCards_NotFound_Licenses.xlsx');
    this.toast.success(`ดาวน์โหลดทะเบียนที่ไม่พบ ${sampleData.length} รายการ`);
  }

  async onExcelSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (!this.selectedSiteId || !this.userId) {
      this.toast.error('กรุณาเลือกลานและเข้าสู่ระบบก่อน');
      return;
    }

    try {
      const items = await this.parseLicensesFromExcel(file);
      if (items.length === 0) {
        this.toast.error('ไม่พบทะเบียนในไฟล์ Excel — ใส่คอลัมน์ ทะเบียน และ vehicle_type (แนะนำเมื่อทะเบียนซ้ำข้ามประเภทรถ)');
        return;
      }

      this.excelFileName = file.name;
      this.excelLicenseItems = items;
      this.runLicenseLookup(items);
    } catch {
      this.toast.error('อ่านไฟล์ Excel ไม่สำเร็จ');
    }
  }

  runLicenseLookup(items: ExcelLicenseItem[]): void {
    this.isLookingUp = true;
    this.apiService.lookupParkingCardsByLicenses({
      siteId: this.selectedSiteId,
      userId: this.userId,
      items: items.map((x) => ({
        license: x.license,
        vehicleTypeId: x.vehicleTypeId ?? null,
        vehicleTypeName: x.vehicleTypeName ?? null,
      })),
    }).subscribe({
      next: (res) => {
        this.applyLookupResult(res);
        this.isLookingUp = false;
        this.toast.success(
          `ค้นหาแล้ว: พบ ${this.lookupFound.length} บัตร / ไม่พบ ${this.lookupNotFound.length} ทะเบียน (จาก ${items.length} แถว Excel)`,
        );
      },
      error: (err) => {
        this.isLookingUp = false;
        this.toast.error(err?.error?.message || 'ค้นหาทะเบียนไม่สำเร็จ');
      },
    });
  }

  openEditModal(mode: BulkEditMode, scopeCardIds?: number[]): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    const ids = (scopeCardIds?.length ? scopeCardIds : Array.from(this.selectedIds))
      .filter((id) => id > 0);
    if (ids.length === 0) {
      this.toast.error('กรุณาเลือกบัตรอย่างน้อย 1 ใบ');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }

    if (mode === 'subscription') {
      const vtIds = this.collectVehicleTypeIdsForCards(ids);
      if (vtIds.size > 1) {
        this.toast.error('แก้ Subscription ทีละกลุ่มประเภทรถ — ใช้ปุ่มในกลุ่ม Excel หรือเลือกบัตรประเภทเดียวกัน');
        return;
      }
    }

    this.editScopeCardIds = ids;
    this.editMode = mode;
    this.editIssueDate = '';
    this.editExpireDate = '';
    this.editSubscriptionId = 0;
    this.editClientId = 0;
    this.editClientSearchText = '';
    this.isEditClientDropdownOpen = false;
    this.isEditModalOpen = true;
  }

  closeEditModal(): void {
    this.isEditModalOpen = false;
    this.editMode = null;
    this.editScopeCardIds = [];
    this.isEditClientDropdownOpen = false;
  }

  openEditModalForGroup(mode: BulkEditMode, group: LookupFoundGroup): void {
    const ids = group.rows.map((r) => r.cardId).filter((id) => id > 0);
    group.rows.forEach((r) => this.selectedIds.add(r.cardId));
    this.openEditModal(mode, ids);
  }

  isGroupAllSelected(group: LookupFoundGroup): boolean {
    if (group.rows.length === 0) return false;
    return group.rows.every((r) => this.selectedIds.has(r.cardId));
  }

  toggleSelectGroup(group: LookupFoundGroup, checked: boolean): void {
    if (checked) group.rows.forEach((r) => this.selectedIds.add(r.cardId));
    else group.rows.forEach((r) => this.selectedIds.delete(r.cardId));
  }

  onEditModalSave(): void {
    if (!this.editMode) return;

    if (this.editMode === 'dates') {
      if (!this.editIssueDate || !this.editExpireDate) {
        this.toast.error('กรุณาระบุวันเริ่มสิทธิ์และวันหมดอายุ');
        return;
      }
      if (this.editExpireDate < this.editIssueDate) {
        this.toast.error('วันหมดอายุต้องไม่ก่อนวันเริ่มสิทธิ์');
        return;
      }
    }
    if (this.editMode === 'subscription' && this.editSubscriptionId <= 0) {
      this.toast.error('กรุณาเลือก Subscription');
      return;
    }
    if (this.editMode === 'client' && this.editClientId <= 0) {
      this.toast.error('กรุณาเลือก Client');
      return;
    }

    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isConfirmModalOpen = true;
  }

  closeConfirmModal(): void {
    this.isConfirmModalOpen = false;
  }

  confirmSave(): void {
    this.isConfirmModalOpen = false;

    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const cardIds = this.editScopeCardIds.length > 0
      ? this.editScopeCardIds
      : Array.from(this.selectedIds);
    const count = cardIds.length;
    this.isSaving = true;
    this.apiService.bulkUpdateParkingCards({
      userId: this.userId,
      menuId,
      siteId: this.selectedSiteId,
      cardIds,
      updateDates: this.editMode === 'dates',
      issueDate: this.editMode === 'dates' ? this.toApiDateTime(this.editIssueDate, '00:00:00') : null,
      expireDate: this.editMode === 'dates' ? this.toApiDateTime(this.editExpireDate, '23:59:00') : null,
      updateSubscription: this.editMode === 'subscription',
      subscriptionId: this.editMode === 'subscription' ? this.editSubscriptionId : 0,
      updateClient: this.editMode === 'client',
      clientId: this.editMode === 'client' ? this.editClientId : 0,
    }).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res?.isSuccess) {
          this.toast.success(res.message || `อัปเดตเรียบร้อย ${count} ใบ`);
          this.closeEditModal();
          this.selectedIds.clear();
          this.loadTable();
          if (this.excelLicenseItems.length > 0) this.runLicenseLookup(this.excelLicenseItems);
        } else {
          this.toast.error(res?.message || 'อัปเดตไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving = false;
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  loadClients(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.clients = [];
      return;
    }
    this.apiLegacy.GetClientIInfo(siteId).subscribe({
      next: (res) => {
        this.clients = res ?? [];
        if (this.selectedClientId > 0) {
          const selected = this.clients.find((c) => c.id === this.selectedClientId);
          if (selected) this.clientSearchText = selected.name;
        }
      },
      error: () => (this.clients = []),
    });
  }

  loadSubscriptions(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.subscriptions = [];
      return;
    }
    this.apiService.getAllParkingSubscriptionsBySite(siteId).subscribe({
      next: (res) => {
        const raw = (res ?? []) as Array<IdNameOption & Record<string, unknown>>;
        this.subscriptions = raw.map((x) => ({
          id: Number(x.id ?? x['Id'] ?? 0),
          name: String(x.name ?? x['Name'] ?? ''),
          vehicleTypeId: Number(x['vehicleTypeId'] ?? x['VehicleTypeId'] ?? 0) || undefined,
        })).filter((x) => x.id > 0);
      },
      error: () => (this.subscriptions = []),
    });
  }

  selectClient(client: ClientIInfo): void {
    this.selectedClientId = client.id;
    this.clientSearchText = client.name;
    this.isClientDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  clearClientFilter(): void {
    this.selectedClientId = 0;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  loadVehicleTypes(): void {
    this.apiService.getParkingVehicleTypes().subscribe({
      next: (res) => {
        this.vehicleTypes = (res ?? []).map((row) => {
          const r = row as ParkingVehicleTypeResponse & Record<string, unknown>;
          return {
            id: Number(r.id ?? r['Id'] ?? 0),
            typeName: String(r.typeName ?? r['TypeName'] ?? r['name'] ?? ''),
            icon: String(r.icon ?? r['Icon'] ?? ''),
          };
        }).filter((v) => v.id > 0);
        if (this.selectedVehicleTypeId > 0) {
          const selected = this.vehicleTypes.find((v) => v.id === this.selectedVehicleTypeId);
          if (selected) this.vehicleTypeSearchText = selected.typeName;
        }
      },
      error: () => (this.vehicleTypes = []),
    });
  }

  selectVehicleType(vehicleType: ParkingVehicleTypeResponse): void {
    this.selectedVehicleTypeId = vehicleType.id;
    this.vehicleTypeSearchText = vehicleType.typeName;
    this.isVehicleTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  clearVehicleTypeFilter(): void {
    this.selectedVehicleTypeId = 0;
    this.vehicleTypeSearchText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  selectEditClient(client: ClientIInfo): void {
    this.editClientId = client.id;
    this.editClientSearchText = client.name;
    this.isEditClientDropdownOpen = false;
  }

  clearEditClient(): void {
    this.editClientId = 0;
    this.editClientSearchText = '';
    this.isEditClientDropdownOpen = false;
  }

  trackById(_: number, item: CityParkingCards): number {
    return item.id;
  }

  trackByCardId(_: number, item: ExcelLookupFoundRow): number {
    return item.cardId;
  }

  plateOf(card: CityParkingCards): string {
    const plate = card.licenseList?.[0]?.plateNumber?.trim();
    return plate || '—';
  }

  getStatusText(status: number): string {
    switch (status) {
      case 0: return 'หมดอายุ';
      case 1: return 'ปกติ';
      case 2: return 'ยกเลิก';
      case 3: return 'ระงับใช้งาน';
      default: return 'หมดอายุ';
    }
  }

  private filterClients(searchText: string, keepSelectedId: number): ClientIInfo[] {
    const sorted = [...this.clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    const q = searchText.trim().toLowerCase();
    if (!q) {
      const preview = sorted.slice(0, ParkingCardsBulkDatesComponent.CLIENT_OPTIONS_PREVIEW);
      if (keepSelectedId > 0 && !preview.some((c) => c.id === keepSelectedId)) {
        const selected = sorted.find((c) => c.id === keepSelectedId);
        if (selected) return [selected, ...preview];
      }
      return preview;
    }
    return sorted.filter((c) => c.name.toLowerCase().includes(q));
  }

  private applyLookupResult(res: LookupParkingCardsByLicensesDto): void {
    const raw = res as LookupParkingCardsByLicensesDto & Record<string, unknown>;
    const foundRaw = (res.found ?? raw['Found'] ?? []) as LookupParkingCardFoundDto[];
    const notFoundRaw = (res.notFound ?? raw['NotFound'] ?? []) as LookupParkingCardNotFoundDto[];

    this.lookupFound = foundRaw.map((f) => {
      const r = f as LookupParkingCardFoundDto & Record<string, unknown>;
      return {
        inputLicense: String(f.inputLicense ?? r['InputLicense'] ?? ''),
        normalizedLicense: String(f.normalizedLicense ?? r['NormalizedLicense'] ?? ''),
        cardId: Number(f.cardId ?? r['CardId'] ?? 0),
        cardName: String(f.cardName ?? r['CardName'] ?? ''),
        cardCode: String(f.cardCode ?? r['CardCode'] ?? ''),
        plateNumber: String(f.plateNumber ?? r['PlateNumber'] ?? ''),
        clientName: String(f.clientName ?? r['ClientName'] ?? ''),
        memberName: String(f.memberName ?? r['MemberName'] ?? ''),
        subscriptionId: Number(f.subscriptionId ?? r['SubscriptionId'] ?? 0),
        subscriptionName: String(f.subscriptionName ?? r['SubscriptionName'] ?? ''),
        vehicleTypeId: Number(f.vehicleTypeId ?? r['VehicleTypeId'] ?? 0),
        vehicleTypeName: String(f.vehicleTypeName ?? r['VehicleTypeName'] ?? ''),
        issueDate: (f.issueDate ?? r['IssueDate'] ?? null) as string | null,
        expireDate: (f.expireDate ?? r['ExpireDate'] ?? null) as string | null,
        status: Number(f.status ?? r['Status'] ?? 0),
      };
    }).filter((x) => x.cardId > 0);

    this.lookupNotFound = notFoundRaw.map((n) => {
      const r = n as LookupParkingCardNotFoundDto & Record<string, unknown>;
      return {
        inputLicense: String(n.inputLicense ?? r['InputLicense'] ?? ''),
        normalizedLicense: String(n.normalizedLicense ?? r['NormalizedLicense'] ?? ''),
        vehicleTypeName: String(n.vehicleTypeName ?? r['VehicleTypeName'] ?? ''),
        reason: String(n.reason ?? r['Reason'] ?? ''),
      };
    });

    this.lookupFound.forEach((r) => this.selectedIds.add(r.cardId));
  }

  private collectVehicleTypeIdsForCards(cardIds: number[]): Set<number> {
    const idSet = new Set(cardIds);
    const types = new Set<number>();
    for (const card of this.parkingCards) {
      if (idSet.has(card.id) && card.vehicleTypeId) types.add(card.vehicleTypeId);
    }
    for (const row of this.lookupFound) {
      if (idSet.has(row.cardId) && row.vehicleTypeId > 0) types.add(row.vehicleTypeId);
    }
    return types;
  }

  private resolveVehicleTypeFromExcel(raw: string): { vehicleTypeId?: number; vehicleTypeName?: string } {
    const text = (raw ?? '').trim();
    if (!text) return {};
    if (/^\d+$/.test(text)) {
      const id = Number(text);
      const vt = this.vehicleTypes.find((v) => v.id === id);
      return { vehicleTypeId: id, vehicleTypeName: vt?.typeName ?? text };
    }
    const lower = text.toLowerCase();
    const vt = this.vehicleTypes.find((v) => {
      const name = v.typeName.toLowerCase();
      return name === lower || name.includes(lower) || lower.includes(name);
    });
    if (vt) return { vehicleTypeId: vt.id, vehicleTypeName: vt.typeName };
    return { vehicleTypeName: text };
  }

  private async parseLicensesFromExcel(file: File): Promise<ExcelLicenseItem[]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];

    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    if (!rows.length) {
      const matrix = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, { header: 1, defval: '' });
      return this.collectLicensesFromMatrix(matrix);
    }

    const keys = Object.keys(rows[0] ?? {});
    const licenseKey = keys.find((k) => {
      const n = k.trim().toLowerCase();
      return n.includes('ทะเบียน')
        || n.includes('license')
        || n.includes('plate')
        || n === 'licenseplate'
        || n === 'plate_number';
    }) ?? keys[0];

    const vehicleTypeKey = keys.find((k) => {
      const n = k.trim().toLowerCase();
      return n.includes('vehicle_type')
        || n.includes('vehicletype')
        || n.includes('ประเภทรถ')
        || n === 'type';
    });

    if (!licenseKey) return [];

    const seen = new Set<string>();
    const result: ExcelLicenseItem[] = [];
    for (const row of rows) {
      const raw = String(row[licenseKey] ?? '').trim();
      if (!raw) continue;
      const vtRaw = vehicleTypeKey ? String(row[vehicleTypeKey] ?? '').trim() : '';
      const vt = this.resolveVehicleTypeFromExcel(vtRaw);
      const dedupeKey = `${raw.replace(/\s+/g, '').toLowerCase()}|${vt.vehicleTypeId ?? 0}|${(vt.vehicleTypeName ?? '').toLowerCase()}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      result.push({
        license: raw,
        vehicleTypeId: vt.vehicleTypeId,
        vehicleTypeName: vt.vehicleTypeName,
      });
    }
    return result;
  }

  private collectLicensesFromMatrix(matrix: (string | number)[][]): ExcelLicenseItem[] {
    const seen = new Set<string>();
    const result: ExcelLicenseItem[] = [];
    for (const row of matrix) {
      const raw = String(row?.[0] ?? '').trim();
      if (!raw || /ทะเบียน|license|plate/i.test(raw)) continue;
      const vtRaw = String(row?.[1] ?? '').trim();
      const vt = this.resolveVehicleTypeFromExcel(vtRaw);
      const dedupeKey = `${raw.replace(/\s+/g, '').toLowerCase()}|${vt.vehicleTypeId ?? 0}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      result.push({
        license: raw,
        vehicleTypeId: vt.vehicleTypeId,
        vehicleTypeName: vt.vehicleTypeName,
      });
    }
    return result;
  }

  private toApiDateTime(dateOnly: string, timeHms: string): string {
    const day = (dateOnly || '').trim().slice(0, 10);
    return `${day}T${timeHms}`;
  }

  private formatDateDisplay(dateOnly: string): string {
    const day = (dateOnly || '').trim().slice(0, 10);
    if (!day) return '—';
    const [y, m, d] = day.split('-');
    if (!y || !m || !d) return day;
    return `${d}/${m}/${y}`;
  }

  private loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.parkingCards = [];
      return;
    }

    this.isLoading = true;
    const status = this.mapper.mapStatusFilter(this.filterStatus);
    this.apiService.getParkingCardForTable({
      siteId,
      userId: this.userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.mapper.mapSearchField(this.searchType) : undefined,
      searchText: this.searchText.trim() || undefined,
      status,
      clientId: this.selectedClientId > 0 ? this.selectedClientId : undefined,
      vehicleTypeId: this.selectedVehicleTypeId > 0 ? this.selectedVehicleTypeId : undefined,
      cardMode: 1,
      sortColumn: 'id',
      sortDirection: 'desc',
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.parkingCards = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || 1;
        this.lastFetchedAt = new Date();
        this.isLoading = false;
      },
      error: () => {
        this.toast.error('ไม่สามารถโหลดรายการบัตรได้');
        this.isLoading = false;
      },
    });
  }
}
